import { useCallback, useEffect, useState } from 'react';
import { Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { MenuItem, Skeleton, Typography } from '@mui/material';
import { isEmpty, isUndefined } from 'lodash';
import { DELETE_MEASUREMENT_DATES_ENABLER } from 'common/constants/features';
import {
  DEFAULT_MAX_OPTIONS,
  MEASUREMENT_DATE_FILTER,
  SHOW_LESS,
  SHOW_LESS_ID,
  SHOW_MORE,
  SHOW_MORE_ID,
} from 'common/constants/pageFilters';
import FeatureEnabler from 'components/FeatureEnabler';
import { toString } from 'utilities';
import FilterProps from './Filter.props';
import {
  StyledDeleteListItemIcon,
  StyledDeleteMenuItem,
  StyledDivider,
  StyledFormControl,
  StyledIconButton,
  StyledInputLabel,
  StyledListItemIcon,
  StyledMenuItemContent,
  StyledMenuItemText,
  StyledSelect,
  StyledShowMoreLessMenuItem,
  StyledSubtitle,
} from './Filter.style';

const Filter = ({
  id: filterId,
  label = '',
  options,
  addOption = undefined,
  deleteOption,
  handleFilter,
  selectedValue = ' ',
  extraOptions,
  subtitle,
  hideActions = false,
  addOptionLabel,
}: FilterProps) => {
  const [showAll, setShowAll] = useState(false);
  const [open, setOpen] = useState(false);
  const [displayOptions, setDisplayOptions] = useState(options);

  const renderValue = useCallback(
    (value: any) => {
      const currentOption = options.find(o => toString(o.id) === toString(value));

      if (!isEmpty(options) && currentOption) {
        return currentOption.label ?? currentOption.name;
      }

      return `No ${label.toLowerCase()}s available`;
    },
    [label, options]
  );

  useEffect(() => {
    let tempOptions = [...options];
    if (filterId === MEASUREMENT_DATE_FILTER && options.length > DEFAULT_MAX_OPTIONS) {
      if (showAll) {
        tempOptions = [...tempOptions, { name: SHOW_LESS, id: SHOW_LESS_ID }];
      } else {
        tempOptions = [...tempOptions.slice(0, DEFAULT_MAX_OPTIONS), { name: SHOW_MORE, id: SHOW_MORE_ID }];
      }
    }

    setDisplayOptions(tempOptions);
  }, [options, filterId, showAll]);

  if (isUndefined(options)) {
    return <Skeleton variant="text" width={150} height={50} />;
  }

  return (
    <StyledFormControl>
      <StyledInputLabel id={`${filterId}-label`} shrink>
        {label}
      </StyledInputLabel>
      <StyledSelect
        id={filterId}
        variant="standard"
        className="filter"
        value={selectedValue}
        onChange={handleFilter}
        renderValue={renderValue}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        displayEmpty>
        <MenuItem selected disabled value=" ">
          Select a {label}
        </MenuItem>
        {displayOptions.map(({ name, id, isDisabled, isSubOption, icon, onIconClick }, index) => {
          const isShowMoreLess = id === SHOW_MORE_ID || id === SHOW_LESS_ID;
          const MenuItemComponent = isShowMoreLess ? StyledShowMoreLessMenuItem : MenuItem;
          const hasIcon = icon && onIconClick;

          return (
            <MenuItemComponent
              disabled={isDisabled}
              value={id.toString()}
              style={{
                paddingLeft: isSubOption ? '2rem' : '1rem',
              }}
              key={id}
              id={`${filterId}-option-${index + 1}`}
              onClick={() => {
                if (isShowMoreLess) {
                  // Defer to keep menu open when clicking show more/less
                  setTimeout(() => setOpen(true), 0);
                }

                switch (id) {
                  case SHOW_MORE_ID:
                    setShowAll(true);
                    break;

                  case SHOW_LESS_ID:
                    setShowAll(false);
                    break;

                  default:
                    break;
                }
              }}>
              {hasIcon ? (
                <StyledMenuItemContent>
                  <StyledMenuItemText>{name}</StyledMenuItemText>
                  <StyledIconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onIconClick(e);
                      setOpen(false);
                    }}
                    onMouseDown={(e) => {
                      e.stopPropagation();
                    }}>
                    {icon}
                  </StyledIconButton>
                </StyledMenuItemContent>
              ) : (
                name
              )}
            </MenuItemComponent>
          );
        })}
        {addOption && !hideActions && (
          <div>
            <StyledDivider />
            <MenuItem onClick={addOption}>
              <StyledListItemIcon>
                <AddIcon fontSize="small" />
              </StyledListItemIcon>
              <Typography variant="inherit">Add {addOptionLabel ?? label}</Typography>
            </MenuItem>
          </div>
        )}
        {deleteOption && !hideActions && (
          <div>
            <FeatureEnabler enabler={DELETE_MEASUREMENT_DATES_ENABLER} hideComponent>
              <>
                <StyledDivider />
                <StyledDeleteMenuItem onClick={deleteOption}>
                  <StyledDeleteListItemIcon>
                    <RemoveIcon fontSize="small" />
                  </StyledDeleteListItemIcon>
                  <Typography variant="inherit">Delete {label}</Typography>
                </StyledDeleteMenuItem>
              </>
            </FeatureEnabler>
          </div>
        )}
        {/* optional extra option */}
        {extraOptions?.map(({ label, callback, icon }, index) => (
          <span key={label}>
            <StyledDivider />
            <MenuItem
              id={`${filterId}-option-${index + 1}`}
              onClick={() => {
                callback();
                setOpen(false);
              }}>
              {icon && <StyledListItemIcon>{icon}</StyledListItemIcon>}
              <Typography variant="inherit">{label}</Typography>
            </MenuItem>
          </span>
        ))}
      </StyledSelect>
      {/* subtitle option */}
      <StyledSubtitle>
        <Typography variant="caption">{subtitle ?? ' '}</Typography>
      </StyledSubtitle>
    </StyledFormControl>
  );
};

export default Filter;
